// src/Pages/Collections/Collections.jsx
import React, { useEffect, useState, useMemo } from 'react'
import { useAPI } from '../../Contexts/APIContext'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import Loader from '../../Components/GeneralComponents/Loader'
import { useNavigate } from 'react-router-dom'
import { ButtonOutline } from '../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import { ModalWindow } from '../../Components/GeneralComponents/ModalWindow/ModalWindow'
import DebounceSearch from '../../Components/GeneralComponents/DebounceSearch/DebounceSearch'
import errorToast from '../../Components/Toasts/ErrorToast'
import useAdminAccess from '../../services/hooks/useAdminAccess'
import classes from './collection.module.css'

export default function Collection() {
    const pageLimit = 20
    const { getCollections, addCollection } = useAPI()
    const [ref, inView] = useInView({
        threshold: 0,
        rootMargin: '200px',
    })
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { isInternal } = useAdminAccess()
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [collectionName, setCollectionName] = useState('')
    const [isCreating, setIsCreating] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [searchTerm, setSearchTerm] = useState('')

    const {
        data,
        isLoading,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useInfiniteQuery({
        queryKey: ['collections'],
        queryFn: ({ pageParam = 1 }) => getCollections(pageParam, pageLimit),
        getNextPageParam: pageData => {
            const nextPage = pageData.p + 1
            return pageData.p * pageLimit < pageData.lists_count ? nextPage : undefined
        },
    })

    // Fetch next page when scrolling into view
    useEffect(() => {
        if (inView && !searchTerm && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
        }
    }, [inView, fetchNextPage, searchTerm, hasNextPage, isFetchingNextPage])

    // When searching, fetch ALL pages so we can filter the complete list
    useEffect(() => {
        if (searchTerm && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
        }
    }, [searchTerm, hasNextPage, isFetchingNextPage, fetchNextPage])

    // Auto-fetch to fill screen when not searching
    useEffect(() => {
        if (!data || searchTerm) return

        const checkAndFetch = () => {
            const notFillingScreen = document.documentElement.scrollHeight <= window.innerHeight
            if (notFillingScreen && hasNextPage && !isFetchingNextPage) {
                fetchNextPage()
            }
        }

        // Initial check
        checkAndFetch()

        // Interval to keep checking until screen is filled
        const intervalId = setInterval(() => {
            if (!hasNextPage) {
                clearInterval(intervalId)
            } else {
                checkAndFetch()
            }
        }, 300)

        // cleanup interval on unmount or dependency change
        return () => clearInterval(intervalId)
    }, [data, isFetchingNextPage, fetchNextPage, searchTerm, hasNextPage])

    // Filter collections based on search term
    const filteredCollections = useMemo(() => {
        if (!data) return []
        const allCollections = data.pages.flatMap(page => page.lists)
        if (!searchTerm.trim()) return allCollections
        const term = searchTerm.toLowerCase()
        return allCollections.filter(collection => {
            const name = (collection.list?.name || collection.name || '').toLowerCase()
            return name.includes(term)
        })
    }, [data, searchTerm])

    const handleNewCollectionClick = () => {
        setShowCreateModal(true)
        setCollectionName('')
    }

    const handleCreateCollection = async () => {
        if (!collectionName.trim()) return
        
        setIsCreating(true)
        try {
            const result = await addCollection(collectionName.trim(), false, [])
            queryClient.invalidateQueries({
                queryKey: ['collections'],
            })
            setShowCreateModal(false)
            setCollectionName('')
            navigate(`/collections/${result.f_id}`)
        } catch (error) {
            errorToast('Error creating collection: ' + error.message)
        } finally {
            setIsCreating(false)
        }
    }

    const handleCancelCreate = () => {
        setShowCreateModal(false)
        setCollectionName('')
    }


    return (
        <div className={classes.wrapper}>
            <div className={classes.header}>
                <h1>Collections</h1>
                <div className={classes.headerRight}>
                    <div className={classes.searchWrapper}>
                        <DebounceSearch
                            inputValue={searchInput}
                            setInputValue={setSearchInput}
                            searchValue={searchTerm}
                            setSearchValue={setSearchTerm}
                            placeholder="Search Collections..."
                        />
                    </div>
                    <div className={classes.headerButtons}>
                        {isInternal && (
                            <ButtonOutline
                                text="Arrange in App"
                                color="pink"
                                size="medium"
                                onClick={() => navigate('/featured-collection-update')}
                            />
                        )}
                        <ButtonOutline
                            text="+ New Collection"
                            color="pink"
                            size="medium"
                            onClick={handleNewCollectionClick}
                        />
                    </div>
                </div>
            </div>

            <div className={classes.cardsContainer}>
                {filteredCollections.length === 0 && searchTerm && !hasNextPage && !isFetchingNextPage && (
                    <div className={classes.noResults}>
                        No collections match &ldquo;{searchTerm}&rdquo;
                    </div>
                )}
                {filteredCollections.map((collection, index) => {
                    const collectionId = collection.list?.f_id || collection.f_id || `collection-${index}`
                    const name = collection.list?.name || collection.name
                    const feelsCount = collection.feels_count || 0
                    const isPublic = collection.list?.is_public ?? collection.is_public ?? false

                    return (
                        <div
                            key={collectionId}
                            className={classes.collection}
                            onClick={() => navigate(`/collections/${collectionId}`)}
                        >
                            <span className={isPublic ? classes.publicIndicator : classes.privateIndicator}></span>
                            <div className={classes.collectionName}>{name}</div>
                            <div className={classes.collectionFeels}>
                                {feelsCount} Feels
                            </div>
                        </div>
                    )
                })}
                <div ref={ref} className={classes.sentinel}></div>
            </div>

            <Loader isLoading={isLoading || isFetchingNextPage} />

            <ModalWindow 
                open={showCreateModal} 
                onClose={handleCancelCreate}
                rounded={true}
                disableCloseIcon={true}
            >
                <div className={classes.modalContent}>
                    <h2 className={classes.modalTitle}>
                        Create New Collection
                    </h2>
                    
                    <div className={classes.modalInputGroup}>
                        <label className={classes.modalLabel}>
                            Title <span className={classes.modalRequired}>*</span>
                        </label>
                        <input
                            type="text"
                            value={collectionName}
                            onChange={(e) => setCollectionName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && collectionName.trim()) {
                                    handleCreateCollection()
                                }
                            }}
                            placeholder="Enter collection name"
                            className={classes.modalInput}
                            autoFocus
                        />
                    </div>
                    
                    <div className={classes.modalButtonGroup}>
                         <ButtonOutline
                            text={isCreating ? "CREATING..." : "CREATE"}
                            color="pink"
                            size="small"
                            onClick={handleCreateCollection}
                            disable={!collectionName.trim() || isCreating}
                        />
                        <ButtonOutline
                            text="CANCEL"
                            color="red"
                            size="small"
                            onClick={handleCancelCreate}
                        />
                    </div>
                </div>
            </ModalWindow>
        </div>
    )
}